import {
  authSessionSchema,
  projectSummarySchema,
  publishResponseSchema,
  startGenerationResponseSchema,
  versionSummarySchema,
  remainingResponseSchema,
  checkoutSessionSchema,
  healthResponseSchema,
  ledgerPageSchema,
  paymentSummarySchema,
  authUserSchema,
  type AuthSession,
  type AuthUser,
  type RemainingResponse,
  type CheckoutSession,
  type PackOffer,
  type HealthResponse,
  type LedgerPage,
  type PaymentSummary,
} from '@gtexai/shared';
import { z } from 'zod';

/** Empty by default: nginx (container) and vite (dev) both proxy same-origin. */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string = 'unknown',
    readonly statusCode = 0,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The access token lives in memory only.
 *
 * Not localStorage: anything readable by JavaScript is readable by an XSS
 * payload, and a token in localStorage survives the tab that leaked it. Losing
 * it on refresh is fine — the httpOnly refresh cookie mints a new one, which is
 * exactly the trade the cookie exists to make.
 */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the automatic refresh-and-retry. Used by refresh itself. */
  noRetry?: boolean;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await fetch(`${BASE_URL}/api${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'content-type': 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    // Sends the refresh cookie. Without it every reload would be a logout.
    credentials: 'include',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  // One transparent refresh-and-retry on 401. Access tokens last 15 minutes, so
  // without this a tab left open overnight shows an error instead of just
  // working — and the user is still fully authenticated.
  if (response.status === 401 && !options.noRetry && path !== '/auth/refresh') {
    const refreshed = await tryRefresh();
    if (refreshed) return request(path, schema, { ...options, noRetry: true });
  }

  if (!response.ok) throw await toApiError(response);

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError('The server sent a response we did not understand.', 'bad_response');
  }
  return parsed.data;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { code?: string; message?: string };
    return new ApiError(
      body.message ?? `Request failed (${response.status}).`,
      body.code ?? 'unknown',
      response.status,
    );
  } catch {
    return new ApiError(`Request failed (${response.status}).`, 'unknown', response.status);
  }
}

async function tryRefresh(): Promise<boolean> {
  try {
    const session = await request('/auth/refresh', authSessionSchema, {
      method: 'POST',
      noRetry: true,
    });
    accessToken = session.accessToken;
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}

const ok = z.object({ ok: z.literal(true) });

export const api = {
  health: () => request('/health', healthResponseSchema) as Promise<HealthResponse>,

  register: (email: string, password: string) =>
    request('/auth/register', ok, { method: 'POST', body: { email, password } }),

  verify: (token: string) =>
    request('/auth/verify', z.object({ user: authUserSchema }), {
      method: 'POST',
      body: { token },
    }),

  resendVerification: (email: string) =>
    request('/auth/resend-verification', ok, { method: 'POST', body: { email } }),

  login: async (email: string, password: string): Promise<AuthSession> => {
    const session = await request('/auth/login', authSessionSchema, {
      method: 'POST',
      body: { email, password },
      noRetry: true,
    });
    accessToken = session.accessToken;
    return session;
  },

  /** Called on app start to turn the refresh cookie back into a session. */
  restore: async (): Promise<AuthSession | null> => {
    try {
      const session = await request('/auth/refresh', authSessionSchema, {
        method: 'POST',
        noRetry: true,
      });
      accessToken = session.accessToken;
      return session;
    } catch {
      accessToken = null;
      return null;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await request('/auth/logout', ok, { method: 'POST', noRetry: true });
    } finally {
      accessToken = null;
    }
  },

  me: () => request('/auth/me', z.object({ user: authUserSchema })) as Promise<{ user: AuthUser }>,

  forgotPassword: (email: string) =>
    request('/auth/forgot-password', ok, { method: 'POST', body: { email } }),

  resetPassword: (token: string, password: string) =>
    request('/auth/reset-password', ok, { method: 'POST', body: { token, password } }),

  /** Fake-provider only: asks the API to sign and deliver its own webhook. */
  devCompletePayment: (reference: string, packId: string, userId: string) =>
    request('/payments/dev/complete', z.object({ applied: z.boolean() }), {
      method: 'POST',
      body: { reference, packId, userId },
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request('/auth/change-password', z.object({ ok: z.literal(true) }), {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  linkLogin: (token: string) =>
    request('/auth/link-login', authSessionSchema.extend({ landing: z.string() }), {
      method: 'POST',
      body: { token },
      noRetry: true,
    }),
  videoBackgrounds: () =>
    request('/generations/videos/remaining', remainingResponseSchema) as Promise<RemainingResponse>,
  videoRequests: () =>
    request('/videos', z.object({ requests: z.array(z.object({
        id: z.string(),
        prompt: z.string(),
        status: z.string(),
        deliveryNote: z.string().nullable(),
        deliveredAt: z.string().nullable(),
        createdAt: z.string(),
      })) })),
  requestVideo: (prompt: string) =>
    request('/videos', z.object({
        id: z.string(),
        prompt: z.string(),
        status: z.string(),
        deliveryNote: z.string().nullable(),
        deliveredAt: z.string().nullable(),
        createdAt: z.string(),
      }), { method: 'POST', body: { prompt } }),

  adminVideoQueue: () =>
    request(
      '/moderation/videos',
      z.object({
        requests: z.array(
          z.object({
            id: z.string(),
            email: z.string(),
            prompt: z.string(),
            status: z.string(),
            createdAt: z.string(),
          }),
        ),
      }),
    ),
  adminDeliverVideo: (requestId: string, note?: string) =>
    request(`/moderation/videos/${requestId}/deliver`, z.object({ id: z.string(), status: z.string() }), {
      method: 'POST',
      body: note ? { note } : {},
    }),
  adminSetVideoStatus: (requestId: string, status: 'requested' | 'in_progress' | 'cancelled') =>
    request(`/moderation/videos/${requestId}/status`, z.object({ id: z.string(), status: z.string() }), {
      method: 'POST',
      body: { status },
    }),

  // ── Admin. Every one of these 403s unless the caller is on ADMIN_EMAILS,
  // which is what the SPA uses to decide whether the section exists at all.
  adminAccounts: (q?: string) =>
    request(
      `/moderation/accounts${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      z.object({
        accounts: z.array(
          z.object({
            id: z.string(),
            email: z.string(),
            status: z.string(),
            verified: z.boolean(),
            remaining: z.number(),
            projects: z.number(),
            payments: z.number(),
            liveLinks: z.number(),
            noEmail: z.boolean(),
            note: z.string().nullable(),
            lastLoginAt: z.string().nullable(),
            createdAt: z.string(),
          }),
        ),
      }),
    ),
  adminInvite: (email: string, generations: number, password?: string) =>
    request(
      '/moderation/accounts',
      z.object({ email: z.string(), status: z.string(), detail: z.string().optional() }),
      { method: 'POST', body: { email, generations, ...(password ? { password } : {}) } },
    ),
  adminSetPassword: (userId: string, password: string) =>
    request(`/moderation/accounts/${userId}/password`, z.object({ ok: z.literal(true) }), {
      method: 'POST',
      body: { password },
    }),
  adminImport: (csv: string, noEmail = false, note = 'imported') =>
    request(
      '/moderation/accounts/import',
      z.object({
        results: z.array(
          z.object({ email: z.string(), status: z.string(), detail: z.string().optional() }),
        ),
      }),
      { method: 'POST', body: { csv, noEmail, note } },
    ),
  adminChangeEmail: (userId: string, email: string) =>
    request(`/moderation/accounts/${userId}`, z.object({ email: z.string() }), {
      method: 'PATCH',
      body: { email },
    }),
  adminSetVerified: (userId: string, verified: boolean) =>
    request(`/moderation/accounts/${userId}/verify`, z.object({ ok: z.literal(true) }), {
      method: verified ? 'POST' : 'DELETE',
    }),
  adminSendResetLink: (userId: string) =>
    request(`/moderation/accounts/${userId}/reset-link`, z.object({ email: z.string() }), {
      method: 'POST',
    }),
  adminLoginLink: (userId: string, days = 14, landing = '/dashboard/billing') =>
    request(
      `/moderation/accounts/${userId}/login-link`,
      z.object({ url: z.string(), email: z.string(), expiresAt: z.string() }),
      { method: 'POST', body: { days, landing } },
    ),
  adminLoginLinks: (days = 14, landing = '/dashboard/billing', userIds?: string[]) =>
    request(
      '/moderation/accounts/login-links',
      z.object({
        links: z.array(z.object({ email: z.string(), url: z.string(), expiresAt: z.string() })),
        skipped: z.array(z.object({ email: z.string(), reason: z.string() })),
      }),
      { method: 'POST', body: { days, landing, ...(userIds ? { userIds } : {}) } },
    ),
  adminRevokeLoginLinksFor: (userIds: string[]) =>
    request(
      '/moderation/accounts/revoke-login-links',
      z.object({ revoked: z.number(), accounts: z.number() }),
      { method: 'POST', body: { userIds } },
    ),
  adminSetNote: (userId: string, note: string) =>
    request(`/moderation/accounts/${userId}/note`, z.object({ note: z.string().nullable() }), {
      method: 'PATCH',
      body: { note },
    }),
  adminRevokeLoginLinks: (userId: string) =>
    request(`/moderation/accounts/${userId}/login-link`, z.object({ revoked: z.number() }), {
      method: 'DELETE',
    }),
  adminSuspendAccount: (userId: string) =>
    request(`/moderation/accounts/${userId}/suspend`, z.object({ ok: z.literal(true) }), {
      method: 'POST',
    }),
  adminRestoreAccount: (userId: string) =>
    request(`/moderation/accounts/${userId}/suspend`, z.object({ ok: z.literal(true) }), {
      method: 'DELETE',
    }),
  adminEraseAccount: (userId: string, confirmEmail: string) =>
    request(
      `/moderation/accounts/${userId}`,
      z.object({ projectsRemoved: z.number(), filesRemoved: z.number() }),
      { method: 'DELETE', body: { confirmEmail } },
    ),

  providers: () =>
    request(
      '/auth/providers',
      z.object({ google: z.boolean(), paymentsMode: z.enum(['live', 'test']) }),
    ),

  remaining: () =>
    request('/generations/remaining', remainingResponseSchema) as Promise<RemainingResponse>,

  ledger: (cursor?: string) =>
    request(
      `/generations/ledger${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      ledgerPageSchema,
    ) as Promise<LedgerPage>,

  packs: () =>
    request('/generations/packs', z.object({ packs: z.array(z.custom<PackOffer>()) })) as Promise<{
      packs: PackOffer[];
    }>,

  checkout: (packId: string) =>
    request('/payments/checkout', checkoutSessionSchema, {
      method: 'POST',
      body: { packId },
    }) as Promise<CheckoutSession>,

  createProject: (prompt: string) =>
    request('/projects', startGenerationResponseSchema, { method: 'POST', body: { prompt } }),

  projects: () => request('/projects', z.object({ projects: z.array(projectSummarySchema) })),

  /** Starts an edit. `targetGenId` scopes it to a section the user clicked. */
  editProject: (projectId: string, prompt: string, targetGenId?: string) =>
    request(`/projects/${projectId}/generations`, startGenerationResponseSchema, {
      method: 'POST',
      body: { prompt, ...(targetGenId ? { targetGenId } : {}) },
    }),

  versions: (projectId: string) =>
    request(
      `/projects/${projectId}/versions`,
      z.object({ versions: z.array(versionSummarySchema) }),
    ),

  restoreVersion: (projectId: string, versionId: string) =>
    request(
      `/projects/${projectId}/versions/${versionId}/restore`,
      z.object({ versions: z.array(versionSummarySchema) }),
      { method: 'POST' },
    ),

  publish: (projectId: string) =>
    request(`/projects/${projectId}/publish`, publishResponseSchema, { method: 'POST' }),

  unpublish: (projectId: string) =>
    request(`/projects/${projectId}/unpublish`, z.object({ ok: z.literal(true) }), {
      method: 'POST',
    }),

  /**
   * Downloads the export and hands back a blob.
   *
   * Not a plain link. The access token lives in memory only — deliberately, so
   * an XSS payload cannot read it — which means an `<a href>` would arrive
   * unauthenticated and 401. Fetching it here keeps the same auth path as every
   * other call, including the transparent refresh-and-retry, at the cost of the
   * zip passing through memory. Exports are a few hundred kilobytes.
   */
  exportVersion: async (projectId: string, versionId: string): Promise<Blob> => {
    const url = `${BASE_URL}/api/projects/${projectId}/versions/${versionId}/export`;
    const send = () =>
      fetch(url, {
        headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
        credentials: 'include',
      });

    let response = await send();
    if (response.status === 401 && (await tryRefresh())) response = await send();
    if (!response.ok) throw await toApiError(response);
    return response.blob();
  },

  payments: () =>
    request('/payments/history', z.object({ payments: z.array(paymentSummarySchema) })) as Promise<{
      payments: PaymentSummary[];
    }>,
};
